import { Arrow, Figure, Gate, INK_FAINT, Lane, Node, Note, Pill } from './primitives';

/**
 * One question, from the call that asks it to the cited answer.
 *
 * The read ledger is drawn as a thing rather than as a step, because it is the only state the loop
 * keeps and it is what the final answer is checked against — the citation guarantee is a consequence
 * of that box existing, not of anything the model was told.
 */
export function RetrievalPath() {
  return (
    <Figure
      title="The retrieval path, from caller to cited answer"
      width={760}
      height={420}
      caption="Nothing here writes. The only state the loop keeps is what it has read — and that state is what the final answer is checked against."
    >
      <Note x={0} y={14} lines="CALLERS" size={9} weight={700} color={INK_FAINT} />
      <Node x={0} y={26} w={148} h={26} title="POST /kb/search" tone="muted" mono />
      <Node x={0} y={58} w={148} h={26} title="MCP kb_ask" tone="muted" mono />
      <Node x={0} y={90} w={148} h={26} title="automation kb_ask" tone="muted" mono />
      <Node x={0} y={122} w={148} h={26} title="kb_ask_watch" tone="muted" mono />

      <Arrow points={[[148, 39], [170, 39], [170, 80]]} />
      <Arrow points={[[148, 71], [162, 71], [162, 80]]} />
      <Arrow points={[[148, 103], [162, 103], [162, 96]]} />
      <Arrow points={[[148, 135], [170, 135], [170, 96]]} />

      <Node x={182} y={62} w={162} h={52} title="ReadScope" sub="ids · label() · strip()" tone="info" mono />
      <Note
        x={263}
        y={132}
        anchor="middle"
        lines={['cone: this project, its ancestors', 'and its sub-projects. self: alone.']}
        size={9}
      />

      <Arrow points={[[344, 88], [400, 88]]} />
      <Lane x={400} y={20} w={360} h={190} label="one agent loop · read-only" tone="accent" />
      <Node x={418} y={40} w={152} h={26} title="kb_outline" tone="muted" mono />
      <Node x={418} y={74} w={152} h={26} title="kb_search ×12" tone="muted" mono />
      <Node x={418} y={108} w={152} h={26} title="kb_grep" tone="muted" mono />
      <Node x={418} y={142} w={152} h={26} title="kb_read ×6" tone="muted" mono />

      <Node x={598} y={74} w={142} h={56} title="read ledger" sub="label → node" tone="info" />
      <Arrow points={[[570, 155], [660, 155], [660, 130]]} label="every body opened" labelAt={[578, 150]} labelAnchor="start" />

      <Node x={470} y={176} w={200} h={24} title="kb_answer" tone="success" mono />
      <Arrow points={[[690, 130], [690, 188], [670, 188]]} label="citable set" labelAt={[698, 162]} labelAnchor="start" />

      <Arrow points={[[570, 210], [570, 238]]} />
      <Gate x={480} y={238} w={180} h={40} title="answered?" />
      <Arrow points={[[660, 258], [716, 258], [716, 296]]} label="yes" labelAt={[694, 252]} />
      <Arrow points={[[480, 258], [300, 258], [300, 290]]} label="no — step cap, or a crash" labelAt={[392, 252]} />

      <Node x={180} y={290} w={240} h={54} title="runFallback" sub="one search · top 6 · one synthesis" tone="danger" mono />
      <Note
        x={300}
        y={362}
        anchor="middle"
        lines={['reuses whatever the loop already read;', 'synthesis fails → hand back the source list']}
        size={9}
      />

      <Arrow points={[[420, 317], [552, 317]]} />
      <Node x={552} y={296} w={208} h={54} title="KbSearchResult" sub="answer · citations · usage" tone="success" mono />

      <Pill x={0} y={298} label="fallbackUsed" tone="danger" />
      <Note x={0} y={332} lines={['the one field that tells', 'the two answers apart']} size={9} />
    </Figure>
  );
}

/**
 * How one query becomes a ranked page list.
 *
 * Two channels side by side over a fusion band, because the single most important fact is that they
 * are combined by *rank* rather than by score — and the second is that the lexical channel matches
 * on a different query from the one it orders by.
 */
export function HybridSearch() {
  return (
    <Figure
      title="Hybrid search: two channels fused by reciprocal rank"
      width={760}
      height={344}
      caption="RRF scores by rank, not by relevance number: cosine is bounded around 0–1 and ts_rank_cd is unbounded and corpus-dependent, so calibrating them against each other drifts as the corpus grows."
    >
      <Node x={280} y={8} w={200} h={28} title="query" tone="accent" />
      <Arrow points={[[380, 36], [380, 56]]} />

      <Node
        x={216}
        y={56}
        w={328}
        h={46}
        title="websearch_to_tsquery"
        sub="strict = a & b & c   ·   loose = a | b | c"
        tone="info"
        mono
      />
      <Note x={556} y={74} lines={['negation is never widened:', "'foo' & !'bar' stays strict"]} size={9} />

      <Arrow points={[[300, 102], [300, 128]]} />
      <Lane x={0} y={128} w={368} h={102} label="lexical" />
      <Node x={16} y={150} w={336} h={28} title="WHERE searchVector @@ loose" tone="muted" mono />
      <Node x={16} y={186} w={336} h={28} title="ORDER BY @@ strict, ts_rank_cd(loose)" tone="muted" mono />

      <Arrow points={[[460, 102], [460, 128]]} />
      <Lane x={392} y={128} w={368} h={102} label="dense" />
      <Node x={408} y={150} w={336} h={28} title="embeddingLarge::halfvec(3072) <=> q" tone="muted" mono />
      <Node x={408} y={186} w={336} h={28} title="LIMIT pool = min(50 × levels, 400)" tone="muted" mono />

      <Note x={0} y={250} lines="match on any term, rank on all of them" size={9} color="var(--color-info-text)" />
      <Note x={760} y={250} anchor="end" lines="the cast is the expression the HNSW index is on" size={9} color="var(--color-info-text)" />

      <Arrow points={[[184, 230], [184, 264], [300, 264], [300, 278]]} />
      <Arrow points={[[460, 230], [460, 278]]} />

      <Node
        x={196}
        y={278}
        w={368}
        h={46}
        title="score = 1/(k + lexRank) + 1/(k + vecRank)"
        sub="k = 60 · FULL OUTER JOIN — either channel alone still survives"
        tone="success"
        mono
      />

      <Note x={0} y={292} lines={['a node both', 'channels like', 'beats one a single', 'channel loves']} size={9} />
      <Note x={760} y={292} anchor="end" lines={['ts_headline uses', 'the loose query, so', 'a partial match still', 'gets a real snippet']} size={9} />
    </Figure>
  );
}
